import React, { useState, useRef, useEffect } from "react";
import { useGame } from "../context/GameContext";

const BLOCK_TYPES = {
  h1: { label: "H1", style: { fontSize: "2rem", fontWeight: "bold", color: "inherit" } },
  h2: { label: "H2", style: { fontSize: "1.5rem", fontWeight: "bold", color: "inherit" } },
  h3: { label: "H3", style: { fontSize: "1.2rem", fontWeight: "bold", color: "inherit" } },
  p: { label: "Text", style: { fontSize: "1rem", color: "inherit" } },
  bullet: { label: "Bullet", style: { fontSize: "1rem", color: "inherit" } },
  numbered: { label: "Numbered", style: { fontSize: "1rem", color: "inherit" } },
  toggle: { label: "Toggle", style: { fontSize: "1rem", color: "inherit" } },
  video: { label: "Video", style: {} },
};

function getYouTubeId(url) {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  return match ? match[1] : null;
}

function Block({ block, index, onChange, onKeyDown, onFocus, focused, onTypeChange, onDelete, numberedIndex }) {
  const ref = useRef();

  useEffect(() => {
    if (focused && ref.current && block.type !== "video") {
      ref.current.focus();
      const len = ref.current.innerText.length;
      const sel = window.getSelection();
      const range = document.createRange();
      if (ref.current.childNodes.length > 0) {
        range.setStart(ref.current.childNodes[0], Math.min(len, ref.current.innerText.length));
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }, [focused]);

  const youtubeId = block.type === "video" ? getYouTubeId(block.content) : null;

  return (
    <div className={`notion-block-row ${focused ? "focused" : ""}`}>
      <div className="notion-block-controls">
        <select
          className="notion-type-select"
          value={block.type}
          onChange={e => onTypeChange(index, e.target.value)}
        >
          {Object.entries(BLOCK_TYPES).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
        <button className="notion-delete-btn" onClick={() => onDelete(index)}>�</button>
      </div>

      <div className="notion-block-content">
        {block.type === "bullet" && <span className="notion-bullet">�</span>}
        {block.type === "numbered" && <span className="notion-bullet">{numberedIndex}.</span>}
        {block.type === "toggle" && <span className="notion-bullet">?</span>}

        {block.type === "video" ? (
          <div className="notion-video-block">
            <input
              className="notion-video-input"
              placeholder="Paste YouTube URL here..."
              value={block.content}
              onChange={e => onChange(index, e.target.value)}
            />
            {youtubeId && (
              <div className="notion-video-embed">
                <iframe
                  width="100%"
                  height="400"
                  src={`https://www.youtube.com/embed/${youtubeId}`}
                  frameBorder="0"
                  allowFullScreen
                  title="video"
                />
              </div>
            )}
          </div>
        ) : (
          <div
            ref={ref}
            className="notion-editable"
            contentEditable
            suppressContentEditableWarning
            style={BLOCK_TYPES[block.type]?.style}
            onInput={e => onChange(index, e.currentTarget.innerText)}
            onKeyDown={e => onKeyDown(e, index)}
            onFocus={() => onFocus(index)}
            dangerouslySetInnerHTML={{ __html: block.content }}
          />
        )}
      </div>
    </div>
  );
}

export default function Playbook() {
  const { categories, playbook, updatePlaybook } = useGame();
  const [selectedCat, setSelectedCat] = useState(null);
  const [focusedBlock, setFocusedBlock] = useState(null);
  const [saving, setSaving] = useState(false);

  const currentCat = categories.find(c => c.id === selectedCat) || categories[0];

  const getBlocks = (catName) => {
    return playbook?.[catName]?.blocks || [{ id: Date.now().toString(), type: "h1", content: catName }];
  };

  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    if (currentCat) {
      setBlocks(getBlocks(currentCat.name));
      setFocusedBlock(null);
    }
  }, [currentCat?.id, playbook]);

  const saveBlocks = async (newBlocks) => {
    setSaving(true);
    const section = { ...(playbook?.[currentCat.name] || {}), blocks: newBlocks };
    await updatePlaybook(currentCat.name, section);
    setSaving(false);
  };

  const handleChange = (index, value) => {
    const updated = blocks.map((b, i) => i === index ? { ...b, content: value } : b);
    setBlocks(updated);
    saveBlocks(updated);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const newBlock = { id: Date.now().toString(), type: "p", content: "" };
      const updated = [...blocks.slice(0, index + 1), newBlock, ...blocks.slice(index + 1)];
      setBlocks(updated);
      setFocusedBlock(index + 1);
      saveBlocks(updated);
    }
    if (e.key === "Backspace" && blocks[index].content === "" && blocks.length > 1) {
      e.preventDefault();
      const updated = blocks.filter((_, i) => i !== index);
      setBlocks(updated);
      setFocusedBlock(Math.max(0, index - 1));
      saveBlocks(updated);
    }
  };

  const handleTypeChange = (index, type) => {
    const updated = blocks.map((b, i) => i === index ? { ...b, type } : b);
    setBlocks(updated);
    saveBlocks(updated);
  };

  const handleDelete = (index) => {
    if (blocks.length === 1) return;
    const updated = blocks.filter((_, i) => i !== index);
    setBlocks(updated);
    saveBlocks(updated);
  };

  const addBlock = (type = "p") => {
    const newBlock = { id: Date.now().toString(), type, content: "" };
    const updated = [...blocks, newBlock];
    setBlocks(updated);
    setFocusedBlock(updated.length - 1);
    saveBlocks(updated);
  };

  let numberedCount = 0;

  return (
    <div className="playbook-page-notion">
      <div className="notion-sidebar">
        <div className="notion-sidebar-title">PLAYBOOK</div>
        {categories.map(cat => (
          <div
            key={cat.id}
            className={`notion-sidebar-item ${currentCat?.id === cat.id ? "active" : ""}`}
            style={{ "--cat-color": cat.color }}
            onClick={() => setSelectedCat(cat.id)}
          >
            <span className="notion-sidebar-dot" style={{ background: cat.color }} />
            {cat.name}
          </div>
        ))}
      </div>

      <div className="notion-editor">
        <div className="notion-editor-header" style={{ borderBottom: `3px solid ${currentCat?.color || "#00ff88"}` }}>
          <h1 className="notion-page-title" style={{ color: currentCat?.color || "#00ff88" }}>
            {currentCat?.name?.toUpperCase()}
          </h1>
          {saving && <span className="notion-saving">Saving...</span>}
        </div>

        <div className="notion-blocks">
          {blocks.map((block, index) => {
            if (block.type === "numbered") numberedCount++;
            else numberedCount = 0;
            return (
              <Block
                key={block.id}
                block={block}
                index={index}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={setFocusedBlock}
                focused={focusedBlock === index}
                onTypeChange={handleTypeChange}
                onDelete={handleDelete}
                numberedIndex={numberedCount}
              />
            );
          })}
        </div>

        <div className="notion-add-blocks">
          <span className="notion-add-label">ADD BLOCK:</span>
          {Object.entries(BLOCK_TYPES).map(([key, val]) => (
            <button key={key} className="notion-add-btn" onClick={() => addBlock(key)}>
              {val.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
